import React, { useState, useCallback, useRef, forwardRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import Cropper from "react-easy-crop";
import Modal from "antd/es/modal";
import Slider from "antd/es/slider";
import { isIOS, isAndroid } from "react-device-detect";
import "./ResizeableCropper.scss";

const pica = require("pica")();

const pkg = "cropper";
const noop = () => {};

const MEDIA_CLASS = `${pkg}-media`;
const MODAL_TITLE = "Edit image";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;
const IOS_SIZE_LIMIT = 2048;
const ANDROID_SIZE_LIMIT = 2048;
const BASIC_SIZE_LIMIT = 2048;

const MIN_ROTATE = 0;
const MAX_ROTATE = 360;
const ROTATE_STEP = 1;
const IMAGE_QUALITY_FACTOR = 0.92;

const EasyCrop = forwardRef((props, ref) => {
  const {
    src,
    aspect,
    shape,
    grid,
    hasZoom,
    zoomVal,
    rotateVal,
    setZoomVal,
    setRotateVal,
    onComplete,
  } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });

  const onCropComplete = useCallback(
    (croppedArea, croppedAreaPixels) => {
      onComplete(croppedAreaPixels);
    },
    [onComplete]
  );

  return (
    <Cropper
      ref={ref}
      image={src}
      aspect={aspect}
      cropShape={shape}
      showGrid={grid}
      zoomWithScroll={hasZoom}
      crop={crop}
      zoom={zoomVal}
      rotation={rotateVal}
      onCropChange={setCrop}
      onZoomChange={setZoomVal}
      onRotationChange={setRotateVal}
      onCropComplete={onCropComplete}
      classes={{
        containerClassName: `cropper-container`,
        mediaClassName: `cropper-media`,
      }}
    />
  );
});

EasyCrop.propTypes = {
  src: PropTypes.string,
  aspect: PropTypes.number,
  shape: PropTypes.string,
  grid: PropTypes.bool,
  hasZoom: PropTypes.bool,
  zoomVal: PropTypes.number,
  rotateVal: PropTypes.number,
  setZoomVal: PropTypes.func,
  setRotateVal: PropTypes.func,
  onComplete: PropTypes.func,
};

const ResizeableCropper = forwardRef((props, ref) => {
  const {
    aspect,
    shape,
    grid,
    zoom,
    rotate,
    beforeCrop,
    modalWidth,
    modalOk,
    modalCancel,
    children,
  } = props;

  const hasZoom = zoom === true;
  const hasRotate = rotate === true;

  const modalTextProps = { okText: modalOk, cancelText: modalCancel };
  Object.keys(modalTextProps).forEach((key) => {
    if (!modalTextProps[key]) delete modalTextProps[key];
  });

  const [src, setSrc] = useState("");
  const [zoomVal, setZoomVal] = useState(1);
  const [rotateVal, setRotateVal] = useState(0);
  const beforeUploadRef = useRef();
  const bufferImageRef = useRef();
  const bufferCanvasRef = useRef();
  const fileRef = useRef();
  const resolveRef = useRef(noop);
  const rejectRef = useRef(noop);
  const cropPixelsRef = useRef();

  /**
   * Upload
   */
  const renderUpload = useCallback(() => {
    const upload = Array.isArray(children) ? children[0] : children;
    const { beforeUpload, accept, ...restUploadProps } = upload.props;
    beforeUploadRef.current = beforeUpload;

    return {
      ...upload,
      props: {
        ...restUploadProps,
        accept: accept || "image/*",
        beforeUpload: (file, fileList) =>
          new Promise((resolve, reject) => {
            if (beforeCrop && !beforeCrop(file, fileList)) {
              reject();
              return;
            }

            fileRef.current = file;
            resolveRef.current = resolve;
            rejectRef.current = reject;

            const reader = new FileReader();
            reader.addEventListener("load", () => {
              if (bufferCanvasRef.current && bufferImageRef.current) {
                const img = ReactDOM.findDOMNode(bufferImageRef.current);
                const canvas = ReactDOM.findDOMNode(bufferCanvasRef.current);
                img.src = reader.result;
                img.onload = () => {
                  const { naturalWidth, naturalHeight } = img;
                  const naturalAspectRatio = naturalWidth / naturalHeight;
                  // console.log(
                  //   `W:${naturalWidth}|H:${naturalHeight}|AR:${naturalAspectRatio}`,
                  // );

                  const OS_THRESHOLD = isIOS
                    ? IOS_SIZE_LIMIT
                    : isAndroid
                    ? ANDROID_SIZE_LIMIT
                    : BASIC_SIZE_LIMIT;

                  if (Math.max(naturalHeight, naturalWidth) > OS_THRESHOLD) {
                    console.log("Image thresholds exceeded, resizing image");
                    if (naturalHeight > naturalWidth) {
                      canvas.height = OS_THRESHOLD;
                      canvas.width = naturalAspectRatio * OS_THRESHOLD;
                    } else if (naturalHeight < naturalWidth) {
                      canvas.width = OS_THRESHOLD;
                      canvas.height = OS_THRESHOLD / naturalAspectRatio;
                    } else {
                      canvas.height = OS_THRESHOLD;
                      canvas.width = OS_THRESHOLD;
                    }
                  } else {
                    console.log("Image dimensions are within threshold");
                    canvas.height = naturalHeight;
                    canvas.width = naturalWidth;
                  }

                  pica
                    .resize(img, canvas)
                    .then((finalCanvas) => {
                      setSrc(finalCanvas.toDataURL("image/jpeg"));
                    })
                    .catch((err) => {
                      console.log("Failed while resizing the image.", err);
                      setSrc(reader.result);
                    });
                };
              }
            });
            reader.readAsDataURL(file);
          }),
      },
    };
  }, [beforeCrop, children]);

  /**
   * EasyCrop
   */
  const onComplete = useCallback((croppedAreaPixels) => {
    cropPixelsRef.current = croppedAreaPixels;
  }, []);

  /**
   * Controls
   */
  const isMinZoom = zoomVal === MIN_ZOOM;
  const isMaxZoom = zoomVal === MAX_ZOOM;
  const isMinRotate = rotateVal === MIN_ROTATE;
  const isMaxRotate = rotateVal === MAX_ROTATE;

  const subZoomVal = useCallback(() => {
    if (!isMinZoom) setZoomVal(zoomVal - ZOOM_STEP);
  }, [isMinZoom, zoomVal]);

  const addZoomVal = useCallback(() => {
    if (!isMaxZoom) setZoomVal(zoomVal + ZOOM_STEP);
  }, [isMaxZoom, zoomVal]);

  const subRotateVal = useCallback(() => {
    if (!isMinRotate) setRotateVal(rotateVal - ROTATE_STEP);
  }, [isMinRotate, rotateVal]);

  const addRotateVal = useCallback(() => {
    if (!isMaxRotate) setRotateVal(rotateVal + ROTATE_STEP);
  }, [isMaxRotate, rotateVal]);

  /**
   * Modal
   */
  const onClose = useCallback(() => {
    setSrc("");
    setZoomVal(1);
    setRotateVal(0);
  }, []);

  const handleOk = useCallback(async () => {
    onClose();
    const naturalImg = document.querySelector(`.${MEDIA_CLASS}`);
    let { naturalWidth, naturalHeight } = naturalImg;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // const naturalAspectRatio = naturalWidth / naturalHeight;
    // console.log(
    //   `Cropper:: Input Image resolution ${naturalWidth}*${naturalHeight}, AR: ${naturalAspectRatio}`,
    // );

    let maxLen = Math.sqrt(
      Math.pow(naturalWidth, 2) + Math.pow(naturalHeight, 2)
    );

    canvas.width = maxLen;
    canvas.height = maxLen;

    if (hasRotate && rotateVal > 0 && rotateVal < 360) {
      const halfMax = maxLen / 2;
      ctx.translate(halfMax, halfMax);
      ctx.rotate((rotateVal * Math.PI) / 180);
      ctx.translate(-halfMax, -halfMax);
    }

    const left = (maxLen - naturalWidth) / 2;
    const top = (maxLen - naturalHeight) / 2;
    ctx.drawImage(naturalImg, left, top);

    const maxImgData = ctx.getImageData(0, 0, maxLen, maxLen);
    const { width, height, x, y } = cropPixelsRef.current;
    canvas.width = width;
    canvas.height = height;
    ctx.putImageData(maxImgData, -left - x, -top - y);

    const { type, name, uid } = fileRef.current;

    canvas.toBlob(
      async (blob) => {
        let newFile = blob;

        newFile.lastModifiedDate = Date.now();
        newFile.name = name;
        newFile.uid = uid;

        if (typeof beforeUploadRef.current !== "function")
          return resolveRef.current(newFile);
        const res = beforeUploadRef.current(
          { newFile: newFile, originalFile: naturalImg },
          [newFile]
        );
        if (typeof res !== "boolean" && !res) {
          console.error("beforeUpload must return a boolean or Promise");
          return;
        }

        if (res === true) return resolveRef.current(newFile);
        if (res === false) return rejectRef.current("not upload");
        if (res && typeof res.then === "function") {
          try {
            const passedFile = await res;
            const type = Object.prototype.toString.call(passedFile);
            if (type === "[object File]" || type === "[object Blob]")
              newFile = passedFile;
            resolveRef.current(newFile);
          } catch (err) {
            rejectRef.current(err);
          }
        }
      },
      type,
      IMAGE_QUALITY_FACTOR
    );
  }, [onClose, hasRotate, rotateVal]);

  return (
    <>
      <img
        alt="hidden"
        ref={bufferImageRef}
        style={{ display: "none" }}
        className="d-none"
        src=""
      />
      <canvas
        ref={bufferCanvasRef}
        style={{ display: "none" }}
        className="d-none"
      ></canvas>

      <>
        {renderUpload()}
        {src && (
          <Modal
            visible={true}
            wrapClassName={`${pkg}-modal`}
            title={"Edit Image"}
            width={modalWidth}
            onOk={handleOk}
            onCancel={onClose}
            maskClosable={false}
            destroyOnClose
            {...modalTextProps}
          >
            <EasyCrop
              ref={ref}
              src={src}
              aspect={aspect}
              shape={shape}
              grid={grid}
              hasZoom={hasZoom}
              zoomVal={zoomVal}
              rotateVal={rotateVal}
              setZoomVal={setZoomVal}
              setRotateVal={setRotateVal}
              onComplete={onComplete}
            />
            {hasZoom && (
              <div className={`${pkg}-control zoom`}>
                <button onClick={subZoomVal} disabled={isMinZoom}>
                  －
                </button>
                <Slider
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={ZOOM_STEP}
                  value={zoomVal}
                  onChange={setZoomVal}
                />
                <button onClick={addZoomVal} disabled={isMaxZoom}>
                  ＋
                </button>
              </div>
            )}
            {hasRotate && (
              <div className={`${pkg}-control rotate`}>
                <button onClick={subRotateVal} disabled={isMinRotate}>
                  ↺
                </button>
                <Slider
                  min={MIN_ROTATE}
                  max={MAX_ROTATE}
                  step={ROTATE_STEP}
                  value={rotateVal}
                  onChange={setRotateVal}
                />
                <button onClick={addRotateVal} disabled={isMaxRotate}>
                  ↻
                </button>
              </div>
            )}
          </Modal>
        )}
      </>
    </>
  );
});

ResizeableCropper.propTypes = {
  aspect: PropTypes.number,
  shape: PropTypes.oneOf(["rect", "round"]),
  zoom: PropTypes.bool,
  grid: PropTypes.bool,
  rotate: PropTypes.bool,
  beforeCrop: PropTypes.func,
  modalTitle: PropTypes.string,
  modalWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modalOk: PropTypes.string,
  modalCancel: PropTypes.string,
  children: PropTypes.node,
};

ResizeableCropper.defaultProps = {
  aspect: 1,
  shape: "rect",
  grid: false,
  zoom: true,
  rotate: false,
  modalTitle: MODAL_TITLE,
  modalWidth: 520,
};

export default ResizeableCropper;
